import { defineStore } from 'pinia';
import { axiosApi } from 'src/utils/axios';
import { computed, ref } from 'vue';

/**
 * Operator settings, backed by `system_settings` on the server.
 *
 * Rows are declared by migration and only ever updated here — the UI cannot
 * invent a setting, because a knob nothing reads is worse than no knob. Values
 * arrive already cast to their declared type, so a boolean is a boolean and
 * never the string "0".
 */
export type SettingType = 'boolean' | 'number' | 'string' | 'select';

export interface SettingOption {
  label: string;
  value: string | number;
}

export interface Setting {
  id: string;
  name: string;
  value: boolean | number | string | null;
  type: SettingType | null;
  description: string | null;
  group: string;
  options: SettingOption[] | null;
  default: boolean | number | string | null;
  icon: string | null;
  updated_at: string | null;
}

export const useServerSettingsStore = defineStore(
  'server-settings-store',
  () => {
    const settings = ref<Setting[]>([]);
    const loading = ref(false);
    const loadFailed = ref(false);
    // Names currently in flight, so a row can disable just its own control
    // instead of the page freezing on every save.
    const saving = ref<string[]>([]);

    const groups = computed(() => [...new Set(settings.value.map((s) => s.group))]);

    const byGroup = computed(() =>
      groups.value.map((group) => ({
        group,
        settings: settings.value.filter((s) => s.group === group),
      })),
    );

    const getSetting = (name: string) => settings.value.find((setting) => setting.name === name);

    const isSaving = (name: string) => saving.value.includes(name);

    const fetchSettings = async () => {
      loading.value = true;
      try {
        const { data } = await axiosApi.get<{ settings: Setting[] }>('settings');
        settings.value = data.settings;
        loadFailed.value = false;
      } catch (error) {
        // Surfaced rather than swallowed: a settings page that renders an
        // empty list on a failed fetch reads as "nothing is configurable",
        // which is a different and much more alarming statement.
        loadFailed.value = true;
        throw error;
      } finally {
        loading.value = false;
      }
    };

    /**
     * Optimistic: the control moves immediately, and the server's own row
     * replaces it on success. A failure puts the previous value back — a
     * toggle that stays flipped after a failed save is a lie about production.
     */
    const updateSetting = async (name: string, value: boolean | number | string) => {
      const index = settings.value.findIndex((setting) => setting.name === name);
      if (index < 0) return false;
      const previous = settings.value[index]!.value;

      settings.value[index] = { ...settings.value[index]!, value };
      saving.value = [...saving.value, name];
      try {
        const { data } = await axiosApi.put<{ setting: Setting }>(`settings/${name}`, { value });
        settings.value[index] = data.setting;
        return true;
      } catch (error) {
        settings.value[index] = { ...settings.value[index]!, value: previous };
        throw error;
      } finally {
        saving.value = saving.value.filter((entry) => entry !== name);
      }
    };

    /**
     * A value somebody else changed, arriving over the socket. Never touches
     * `saving`: this is not our write, and blocking our own control because a
     * colleague saved theirs would be wrong.
     */
    const applyRemoteValue = (name: string, value: boolean | number | string) => {
      const index = settings.value.findIndex((setting) => setting.name === name);
      if (index < 0) return;
      settings.value[index] = { ...settings.value[index]!, value };
    };

    return {
      settings,
      groups,
      byGroup,
      loading,
      loadFailed,
      saving,
      isSaving,
      getSetting,
      fetchSettings,
      updateSetting,
      applyRemoteValue,
    };
  },
  // Only the data persists. `saving` on disk would survive a tab killed
  // mid-save, and nothing but that save's own `finally` ever clears it — the
  // row's control would come back disabled forever.
  { persist: { pick: ['settings'] } },
);
